// Phase 9B — useKnowledgeBase hook.
//
// Read-only consumer of kb_articles and trust_metrics tables.
// Extends the existing Knowledge page with structured article
// types and trust transparency metrics.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ─────────────────────────────────────────────────────

export type KbArticleType =
  | "methodology"
  | "trust_policy"
  | "faq"
  | "glossary"
  | "tutorial"
  | "release_note"
  | "system_doc";

export type KbStatus = "draft" | "published" | "archived";

export interface KbArticle {
  id: string;
  article_type: KbArticleType;
  status: KbStatus;
  title: string;
  slug: string;
  summary: string | null;
  content: string;
  category: string;
  tags: readonly string[];
  sort_order: number;
  pinned: boolean;
  meta: Record<string, unknown>;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TrustMetric {
  id: string;
  metric_key: string;
  metric_label: string;
  value_num: number | null;
  value_text: string | null;
  category: string;
  description: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// ─── State ─────────────────────────────────────────────────────

export interface UseKbArticlesState {
  articles: readonly KbArticle[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export interface UseTrustMetricsState {
  metrics: readonly TrustMetric[];
  loading: boolean;
  error: string | null;
}

// ─── Parsers ───────────────────────────────────────────────────

function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function asNum(v: unknown): number {
  if (v === null || v === undefined) return 0;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function parseArticle(row: Record<string, unknown>): KbArticle {
  return {
    id: String(row.id ?? ""),
    article_type: (row.article_type as KbArticleType) ?? "system_doc",
    status: (row.status as KbStatus) ?? "published",
    title: String(row.title ?? ""),
    slug: String(row.slug ?? ""),
    summary: asStr(row.summary),
    content: String(row.content ?? ""),
    category: String(row.category ?? "general"),
    tags: Array.isArray(row.tags) ? (row.tags as string[]) : [],
    sort_order: asNum(row.sort_order),
    pinned: row.pinned === true,
    meta: typeof row.meta === "object" && row.meta !== null
      ? (row.meta as Record<string, unknown>)
      : {},
    published_at: asStr(row.published_at),
    created_at: String(row.created_at ?? ""),
    updated_at: String(row.updated_at ?? ""),
  };
}

function parseMetric(row: Record<string, unknown>): TrustMetric {
  return {
    id: String(row.id ?? ""),
    metric_key: String(row.metric_key ?? ""),
    metric_label: String(row.metric_label ?? ""),
    value_num: row.value_num !== null && row.value_num !== undefined
      ? asNum(row.value_num)
      : null,
    value_text: asStr(row.value_text),
    category: String(row.category ?? "transparency"),
    description: asStr(row.description),
    sort_order: asNum(row.sort_order),
    created_at: String(row.created_at ?? ""),
    updated_at: String(row.updated_at ?? ""),
  };
}

// ─── useKbArticles ─────────────────────────────────────────────

export function useKbArticles(
  articleType?: KbArticleType,
): UseKbArticlesState {
  const { user } = useAuthSession();
  const [articles, setArticles] = useState<readonly KbArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setArticles([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      let resp: {
        data: Record<string, unknown>[] | null;
        error: { message: string } | null;
      };

      if (articleType) {
        resp = await (
          client.from("kb_articles" as never) as unknown as {
            select: (cols: string) => {
              eq: (col: string, val: string) => {
                eq: (col: string, val: string) => {
                  order: (col: string, opts: { ascending: boolean }) => Promise<{
                    data: Record<string, unknown>[] | null;
                    error: { message: string } | null;
                  }>;
                };
              };
            };
          }
        )
          .select("*")
          .eq("article_type", articleType)
          .eq("status", "published")
          .order("sort_order", { ascending: true });
      } else {
        resp = await (
          client.from("kb_articles" as never) as unknown as {
            select: (cols: string) => {
              eq: (col: string, val: string) => {
                order: (col: string, opts: { ascending: boolean }) => Promise<{
                  data: Record<string, unknown>[] | null;
                  error: { message: string } | null;
                }>;
              };
            };
          }
        )
          .select("*")
          .eq("status", "published")
          .order("sort_order", { ascending: true });
      }

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setArticles([]);
        setLoading(false);
        return;
      }

      const parsed = (resp.data ?? [])
        .filter((r): r is Record<string, unknown> => r !== null)
        .map(parseArticle);
      // Pinned first
      parsed.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return a.sort_order - b.sort_order;
      });
      setArticles(parsed);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, articleType, tick]);

  return useMemo(
    () => ({ articles, loading, error, refetch }),
    [articles, loading, error, refetch],
  );
}

// ─── useTrustMetrics ───────────────────────────────────────────

export function useTrustMetrics(): UseTrustMetricsState {
  const { user } = useAuthSession();
  const [metrics, setMetrics] = useState<readonly TrustMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setMetrics([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      const resp = await (
        client.from("trust_metrics" as never) as unknown as {
          select: (cols: string) => {
            order: (col: string, opts: { ascending: boolean }) => Promise<{
              data: Record<string, unknown>[] | null;
              error: { message: string } | null;
            }>;
          };
        }
      )
        .select("*")
        .order("sort_order", { ascending: true });

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setMetrics([]);
        setLoading(false);
        return;
      }

      setMetrics(
        (resp.data ?? [])
          .filter((r): r is Record<string, unknown> => r !== null)
          .map(parseMetric),
      );
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  return useMemo(
    () => ({ metrics, loading, error }),
    [metrics, loading, error],
  );
}
